import React, { useEffect, useRef } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface INotesBulletList {
    items: string[];
    onChange: (items: string[]) => void; // parent sets dirty
    canEdit: boolean;
    addLabel?: string;
    // When false, the list renders only its items — the add affordance lives
    // outside (e.g. the row's action-cluster icon button). Defaults to true so
    // the plan-level lists keep their own "Agregar nota" text button.
    showAddButton?: boolean;
}

// A button-added bullet list. When empty (and its add button is hidden) it
// renders nothing at all — the space saving vs. an always-present TextField.
// Read-only mode shows plain bullet Typography and no buttons. Blank bullets are
// allowed while editing and are stripped at save time by joinNotes.
export function NotesBulletList({
    items,
    onChange,
    canEdit,
    addLabel = 'Agregar nota',
    showAddButton = true,
}: INotesBulletList) {
    // Autofocus the trailing bullet right after one is appended, regardless of
    // whether it came from this component's own button or an external trigger
    // (the row's icon button appending via onChange). We detect an append as
    // "length grew by exactly one and the new last item is empty", so a data
    // load (many items at once) or a removal never steals focus — and neither
    // does the initial mount.
    const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
    const prevLenRef = useRef(items.length);
    useEffect(() => {
        const grewByOne = items.length === prevLenRef.current + 1;
        const lastEmpty = items[items.length - 1] === '';
        if (grewByOne && lastEmpty) {
            inputsRef.current[items.length - 1]?.focus();
        }
        prevLenRef.current = items.length;
    }, [items]);

    const handleItemChange = (index: number, value: string) => {
        onChange(items.map((item, i) => (i === index ? value : item)));
    };

    const handleRemove = (index: number) => {
        onChange(items.filter((_, i) => i !== index));
    };

    const handleAdd = () => {
        onChange([...items, '']);
    };

    // Read-only: nothing to show when there are no notes.
    if (!canEdit) {
        if (items.length === 0) return null;
        return (
            <Box>
                {items.map((item, index) => (
                    <Typography key={index} variant={'body2'}>
                        {`• ${item}`}
                    </Typography>
                ))}
            </Box>
        );
    }

    return (
        <Box>
            {items.map((item, index) => (
                <Box
                    key={index}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                >
                    <Typography component={'span'} sx={{ lineHeight: 1 }}>
                        •
                    </Typography>
                    <TextField
                        value={item}
                        onChange={(e) => handleItemChange(index, e.target.value)}
                        size={'small'}
                        margin={'none'}
                        fullWidth
                        inputRef={(el: HTMLInputElement | null) => {
                            inputsRef.current[index] = el;
                        }}
                    />
                    <Tooltip title={'Quitar'}>
                        <span>
                            <IconButton
                                size={'small'}
                                onClick={() => handleRemove(index)}
                            >
                                <DeleteOutlineIcon fontSize={'inherit'} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ))}
            {showAddButton ? (
                <Button
                    variant={'text'}
                    size={'small'}
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    {addLabel}
                </Button>
            ) : null}
        </Box>
    );
}
